import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CheckCheck } from 'lucide-react'
import { useState } from 'react'

import { useToastStack } from '@/components/feedback/toast-stack'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { messages } from '@/lib/messages'
import { toProblem } from '@/lib/problem'
import { hrApi, type HrBulkApproveItem, type HrRequest } from '@/services/http/hr'

const t = messages.hr
const b = t.approvals.bulk

/** Reddedilen talebin nedeni: kaydı değişmişse "yeniden gönderilmeli", değilse sunucunun açıklaması. */
function failureReason(item: HrBulkApproveItem): string {
  return item.code === 'request_stale' ? b.stale : (item.detail ?? item.code ?? '')
}

/**
 * Onaylar'da çoklu seçimle toplu onay: onay penceresi, sonra talep başına sonuç özeti. Her talep
 * sunucuda ayrı doğrulanır; biri reddedilse de diğerleri onaylanır.
 */
export function BulkApproveButton({
  selected,
  requests,
  onDone,
}: {
  selected: string[]
  requests: HrRequest[]
  onDone: () => void
}) {
  const queryClient = useQueryClient()
  const { toast } = useToastStack()
  const [confirming, setConfirming] = useState(false)
  const [result, setResult] = useState<HrBulkApproveItem[] | null>(null)
  const approve = useMutation({
    mutationFn: () => hrApi.bulkApprove(selected),
    onSuccess: (items) => {
      setConfirming(false)
      setResult(items)
      onDone()
      void queryClient.invalidateQueries({ queryKey: ['hr', 'requests'] })
      void queryClient.invalidateQueries({ queryKey: ['hr', 'interventions'] })
    },
    onError: (error) =>
      toast({
        type: 'error',
        title: t.approvals.actionFailed,
        description: toProblem(error).detail ?? undefined,
      }),
  })
  const byId = new Map(requests.map((request) => [request.id, request]))
  const failed = (result ?? []).filter((item) => !item.ok)
  const okCount = (result ?? []).length - failed.length

  return (
    <>
      <Button
        size="sm"
        disabled={selected.length === 0 || approve.isPending}
        onClick={() => setConfirming(true)}
      >
        <CheckCheck /> {b.approve(selected.length)}
      </Button>
      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{b.confirmTitle(selected.length)}</DialogTitle>
            <DialogDescription>{b.confirmBody}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setConfirming(false)}>
              {t.actions.cancel}
            </Button>
            <Button disabled={approve.isPending} onClick={() => approve.mutate()}>
              {b.approve(selected.length)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={result !== null} onOpenChange={(open) => !open && setResult(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{b.resultTitle}</DialogTitle>
            <DialogDescription>
              {b.approved(okCount)}
              {failed.length > 0 && ` · ${b.failed(failed.length)}`}
            </DialogDescription>
          </DialogHeader>
          {failed.length > 0 && (
            <DialogBody>
              <ul className="flex flex-col gap-2 text-sm">
                {failed.map((item) => {
                  const request = byId.get(item.id)
                  return (
                    <li key={item.id} className="flex flex-col">
                      <span className="font-medium">
                        {request ? (request.personel_adi ?? request.sicilno) : item.id}
                      </span>
                      <span className="text-warning text-xs">{failureReason(item)}</span>
                    </li>
                  )
                })}
              </ul>
            </DialogBody>
          )}
          <DialogFooter>
            <Button onClick={() => setResult(null)}>{b.close}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
